import { chakra, type HTMLChakraProps } from '@chakra-ui/react';
import { forwardRef, type ElementType } from 'react';
import { Icon, type LucideIcon } from '../icons/Icon';
import { focusRing } from '../primitives/_shared';

const Root = chakra('a', {
  base: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '3',
    minH: '11', // 44px — comfortable pointer + touch target
    px: '3',
    borderRadius: 'md',
    fontFamily: 'body',
    fontSize: 'md',
    fontWeight: 'medium',
    lineHeight: 'none',
    color: 'text.secondary',
    textDecoration: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    transitionProperty: 'background-color, color',
    transitionDuration: 'fast',
    transitionTimingFunction: 'standard',
    _hover: { bg: 'action.secondary', color: 'text.primary' },
    // Active: a blue selection wash + the action colour on the label/icon (AA-clear,
    // no side-stripe) — mirrors the prior MUI shell's active treatment.
    '&[data-active="true"]': {
      bg: 'action.primary/12',
      color: 'action.primary',
      fontWeight: 'semibold',
      _hover: { bg: 'action.primary/16' },
    },
    // Reveal the collapsed tooltip on hover / keyboard focus.
    '&:hover [data-tip], &:focus-visible [data-tip]': {
      opacity: 1,
      visibility: 'visible',
    },
    ...focusRing,
  },
});

const Label = chakra('span', {
  base: { flex: '1', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' },
});

const CountBadge = chakra('span', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minW: '5',
    h: '5',
    px: '1.5',
    borderRadius: 'full',
    bg: 'status.danger',
    color: 'text.inverse',
    fontSize: '2xs',
    fontWeight: 'bold',
    lineHeight: 'none',
  },
});

/** Collapsed-only hover/focus tooltip anchored to the right of the icon. */
const Tip = chakra('span', {
  base: {
    position: 'absolute',
    left: 'calc(100% + 8px)',
    top: '50%',
    transform: 'translateY(-50%)',
    px: '2.5',
    py: '1',
    borderRadius: 'sm',
    bg: 'surface.inverse',
    color: 'text.inverse',
    fontSize: 'xs',
    fontWeight: 'medium',
    whiteSpace: 'nowrap',
    boxShadow: 'sm',
    opacity: 0,
    visibility: 'hidden',
    pointerEvents: 'none',
    zIndex: 'tooltip',
    transitionProperty: 'opacity',
    transitionDuration: 'fast',
  },
});

export interface NavItemProps {
  label: string;
  icon: LucideIcon;
  /** Marks the current section — sets `aria-current="page"` + the active wash. */
  active?: boolean;
  /** Icon-only mode with a hover/focus tooltip (the collapsed NavRail). */
  collapsed?: boolean;
  badge?: number;
  /** Render as a router `Link` (receives `to`) or default to a plain `<a href>`. */
  as?: ElementType;
  to?: string;
  href?: string;
  onClick?: () => void;
  'data-testid'?: string;
}

/**
 * A single navigation destination — icon + label, or icon-only with a tooltip when the
 * rail is collapsed. The accessible name is always the label (via `aria-label`), so it
 * stays stable for screen readers and E2E selectors whether expanded or collapsed.
 */
export const NavItem = forwardRef<HTMLAnchorElement, NavItemProps>(function NavItem(
  { label, icon, active, collapsed, badge, as, to, href, onClick, ...rest },
  ref,
) {
  const linkProps: HTMLChakraProps<'a'> = as
    ? ({ to } as HTMLChakraProps<'a'>)
    : { href: href ?? to };
  return (
    <Root
      as={as}
      ref={ref}
      {...linkProps}
      onClick={onClick}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      data-active={active ? 'true' : undefined}
      justifyContent={collapsed ? 'center' : undefined}
      px={collapsed ? '0' : undefined}
      {...(rest as Record<string, unknown>)}
    >
      <Icon as={icon} size="md" tone="current" />
      {!collapsed && <Label>{label}</Label>}
      {!collapsed && badge != null && badge > 0 && (
        <CountBadge aria-hidden>{badge > 99 ? '99+' : badge}</CountBadge>
      )}
      {collapsed && (
        <Tip data-tip aria-hidden>
          {label}
        </Tip>
      )}
    </Root>
  );
});
